import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { startSession, endSession } from "@/lib/api";
import { toast } from "sonner";
import { Play, Square, Pause } from "lucide-react";

const WORK_TYPES = [
  "Research", "Coding", "Learning", "Reading", "Automation",
  "Business Analysis", "Discovery", "Videos", "Writing",
  "Prompt Engineering", "Case Study", "Other",
];

export default function WorkSessionDialog({ open, onOpenChange, onComplete }) {
  const [phase, setPhase] = useState("setup"); // setup | running | wrapup
  const [workType, setWorkType] = useState("Coding");
  const [goal, setGoal] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [elapsed, setElapsed] = useState(0); // seconds — derived from wallclock
  const [paused, setPaused] = useState(false);

  // wallclock refs (immune to interval stacking / HMR)
  const segmentStartRef = useRef(null); // ms timestamp when the current running segment began
  const accumulatedRef = useRef(0);     // seconds accumulated from prior segments (pre-pause)

  // wrap-up fields
  const [accomplished, setAccomplished] = useState("");
  const [difficulty, setDifficulty] = useState(3);
  const [hours, setHours] = useState(0);
  const [lessons, setLessons] = useState("");
  const [notes, setNotes] = useState("");
  const [github, setGithub] = useState("");
  const [website, setWebsite] = useState("");

  const computeElapsed = () => {
    if (segmentStartRef.current == null) return accumulatedRef.current;
    const seg = Math.floor((Date.now() - segmentStartRef.current) / 1000);
    return accumulatedRef.current + Math.max(0, seg);
  };

  // Tick the display from wallclock. Even if this effect runs twice, elapsed
  // stays correct because it's derived from Date.now(), not incremented.
  useEffect(() => {
    if (phase !== "running" || paused) return;
    const tick = () => setElapsed(computeElapsed());
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [phase, paused]);

  useEffect(() => {
    if (!open) {
      setPhase("setup"); setGoal(""); setSessionId(null); setElapsed(0); setPaused(false);
      segmentStartRef.current = null; accumulatedRef.current = 0;
      setAccomplished(""); setDifficulty(3); setHours(0); setLessons(""); setNotes(""); setGithub(""); setWebsite("");
    }
  }, [open]);

  const timeStr = useMemo(() => {
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsed]);

  const handleStart = async () => {
    try {
      const s = await startSession(workType, goal);
      setSessionId(s.id);
      accumulatedRef.current = 0;
      segmentStartRef.current = Date.now();
      setElapsed(0);
      setPhase("running");
    } catch { toast.error("Could not start session"); }
  };

  const togglePause = () => {
    if (paused) {
      // resuming — start a new segment
      segmentStartRef.current = Date.now();
      setPaused(false);
    } else {
      // pausing — bank the current segment
      accumulatedRef.current = computeElapsed();
      segmentStartRef.current = null;
      setElapsed(accumulatedRef.current);
      setPaused(true);
    }
  };

  const handleStop = () => {
    const totalSecs = computeElapsed();
    segmentStartRef.current = null;
    accumulatedRef.current = totalSecs;
    setElapsed(totalSecs);
    const h = totalSecs / 3600;
    // round to 2 decimals; enforce a minimum of 0.01h so backend accepts sub-minute sessions
    setHours(Math.max(0.01, +h.toFixed(2)));
    setPhase("wrapup");
  };

  const handleSubmit = async () => {
    try {
      const res = await endSession({
        session_id: sessionId,
        accomplished, difficulty, hours: Number(hours),
        lessons, notes, github_link: github, website,
      });
      toast.success(`+${res.xp_earned} XP · ${res.streak_days} day streak`);
      onOpenChange(false);
      onComplete && onComplete();
    } catch { toast.error("Could not save session"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-lg" data-testid="work-session-dialog">
        {phase === "setup" && (
          <>
            <DialogHeader>
              <DialogTitle className="mono tracking-tight">$ new work session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">Work Type</label>
                <Select value={workType} onValueChange={setWorkType}>
                  <SelectTrigger data-testid="work-type-select" className="bg-muted border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {WORK_TYPES.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline block mb-2">What are you working on?</label>
                <Textarea
                  data-testid="session-goal-input"
                  placeholder="e.g. Build a Zapier flow that turns Gmail leads into Notion rows."
                  value={goal} onChange={(e) => setGoal(e.target.value)}
                  className="bg-muted border-border resize-none" rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="session-start-btn" onClick={handleStart} className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs">
                <Play className="h-4 w-4 mr-2" /> Start Timer
              </Button>
            </DialogFooter>
          </>
        )}
        {phase === "running" && (
          <>
            <DialogHeader>
              <DialogTitle className="mono">$ working · {workType}</DialogTitle>
            </DialogHeader>
            <div className="py-8 flex flex-col items-center gap-4">
              <div className="mono text-6xl font-bold text-primary terminal-glow tabular-nums" data-testid="session-timer">{timeStr}</div>
              {goal && <p className="text-sm text-muted-foreground text-center max-w-sm">{goal}</p>}
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={togglePause} className="border-border" data-testid="session-pause-btn">
                  <Pause className="h-4 w-4 mr-2" /> {paused ? "Resume" : "Pause"}
                </Button>
                <Button data-testid="session-stop-btn" onClick={handleStop} className="bg-destructive text-white hover:bg-destructive/90">
                  <Square className="h-4 w-4 mr-2" /> Stop
                </Button>
              </div>
            </div>
          </>
        )}
        {phase === "wrapup" && (
          <>
            <DialogHeader>
              <DialogTitle className="mono">$ log the run</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              <div>
                <label className="overline block mb-1">What did you accomplish?</label>
                <Textarea data-testid="wrapup-accomplished" value={accomplished} onChange={(e) => setAccomplished(e.target.value)} className="bg-muted border-border resize-none" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1">Hours</label>
                  <Input data-testid="wrapup-hours" type="number" step="0.01" min="0.01" value={hours} onChange={(e) => setHours(e.target.value)} className="bg-muted border-border mono" />
                </div>
                <div>
                  <label className="overline block mb-1">Difficulty {difficulty}/5</label>
                  <Slider value={[difficulty]} min={1} max={5} step={1} onValueChange={(v) => setDifficulty(v[0])} className="mt-3" />
                </div>
              </div>
              <div>
                <label className="overline block mb-1">Lessons learned</label>
                <Textarea data-testid="wrapup-lessons" value={lessons} onChange={(e) => setLessons(e.target.value)} className="bg-muted border-border resize-none" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="wrapup-github" placeholder="GitHub link" value={github} onChange={(e) => setGithub(e.target.value)} className="bg-muted border-border mono text-xs" />
                <Input data-testid="wrapup-website" placeholder="Website / demo" value={website} onChange={(e) => setWebsite(e.target.value)} className="bg-muted border-border mono text-xs" />
              </div>
              <div>
                <label className="overline block mb-1">Notes</label>
                <Textarea data-testid="wrapup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-muted border-border resize-none" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="wrapup-submit-btn" onClick={handleSubmit} className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs">
                Save + Award XP
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
